import classNames from 'classnames';
import { List } from 'immutable';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import createDndScrollZone from 'components/DndScrollZone';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import ScreensDragSpacer from 'components/ScreensDragSpacer';
import ScreensPanelItem from 'components/ScreensPanelItem';
import { ModalType } from 'const';
import { toggleShowScreensPanel } from 'containers/App/actions';
import { showModal } from 'containers/ModalManager/actions';
import { screensPanelSelector } from 'containers/ScreensPanel/selectors';
import { ScreenMap } from 'models';
import { createSmallVerticalStrength } from 'utils/dndScrollZone';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

const DnDScrollZone = createDndScrollZone<React.HTMLAttributes<HTMLElement>>('div');

const ScreensPanel: React.FC = () => {
  const dispatch = useDispatch();
  const {
    activeScreenId,
    artboards,
    projectType,
    screenDefinitions,
    screenNames,
    screens,
    selectedEntityTypesByScreenId,
    showScreensPanel,
    surfaceIds,
    isReadOnly,
  } = useSelector(screensPanelSelector);

  const [dragHotspotPosition, setDragHotspotPosition] = React.useState(0);
  const [isOver, setIsOver] = useState(false);

  const updateHotspotPosition = (position: number): void => {
    setDragHotspotPosition(position);
  };
  const toggleOverState = (over: boolean): void => {
    setIsOver(over);
  };

  const screenNamesList = List(screenNames.valueSeq());

  const renderScreenPanelItems = (dataTestId: string): JSX.Element[] => {
    const isDeletingDisabled = screens.size === 1;

    return screens.valueSeq().toArray().map((screen: ScreenMap, index) => {
      const id = screen.get('id');
      const position = surfaceIds.findIndex(surfaceId => surfaceId === id) + 1;
      const selectedEntityTypes = selectedEntityTypesByScreenId.get(id);
      const showIconComponent = !selectedEntityTypes.isEmpty();
      const entityType = selectedEntityTypes.last();
      const artboardId = screen.get('artboardId');
      const artboardStyles = artboards.getIn([artboardId, 'styles']);

      return (
        <ScreensPanelItem
          screenId={id}
          artboardStyles={artboardStyles}
          entityType={entityType}
          isActive={id === activeScreenId}
          isDeletingDisabled={isDeletingDisabled}
          isOver={isOver}
          key={id}
          position={position}
          projectType={projectType}
          screen={screen}
          isReadOnly={isReadOnly}
          screenDefinitions={screenDefinitions}
          screenNames={screenNamesList}
          showIconComponent={showIconComponent}
          toggleOverState={toggleOverState}
          updateHotspotPosition={updateHotspotPosition}
          dataTestId={`${dataTestId}-screen${index ?? 0}`}
        />
      );
    });
  };

  const togglePanel = (): void => {
    dispatch(toggleShowScreensPanel(!showScreensPanel));
  };

  const renderDragHotspot = (dataTestId: string): JSX.Element => (
    <ScreensDragSpacer position={dragHotspotPosition} dataTestId={dataTestId} />
  );

  return (
    <div
      className={classNames(styles.ScreensPanel, {
        [styles.ScreensPanelHidden]: !showScreensPanel,
        [styles.isReadOnly]: isReadOnly,
      })}
      data-testid="screensPanel"
    >
      <div className={styles.title} data-testid="screensPanel-title">
        {showScreensPanel && intlGet('ScreensPanel', 'Title')}
        <Icon
          size="md"
          color="primary"
          onClick={togglePanel}
          type={showScreensPanel ? IconType.DOUBLE_ARROW_LEFT : IconType.DOUBLE_ARROW_RIGHT}
          dataTestId="screensPanel-title"
        />
      </div>
      <DnDScrollZone
        className={styles.list}
        verticalStrength={createSmallVerticalStrength}
        data-testid="screensPanel-title-list"
      >
        {renderScreenPanelItems('screensPanel-title-list')}
        {isOver && renderDragHotspot('screensPanel-title-list-dragSpacer')}
      </DnDScrollZone>
      {!isReadOnly && (
        <AccessibleDiv
          className={styles.toolbar}
          onClick={(): void => {dispatch(showModal(ModalType.ADD_SCREEN));}}
          data-testid="screensPanel-title-toolbar"
        >
          <div className={styles.iconWrapper} data-testid="screensPanel-title-toolbar-iconWrapper">
            <Icon
              color="secondary"
              size="badge"
              type={IconType.PLUS}
              dataTestId="screensPanel-title-toolbar-iconWrapper"
            />
          </div>
          <span className={styles.toolbarTitle} data-testid="screensPanel-title-toolbar-title">
            {intlGet('ScreensPanel.Actions', 'Add')}
          </span>
        </AccessibleDiv>
      )}
    </div>
  );
};

export default ScreensPanel;
